import { useEffect, useState } from 'react';
import axios from 'axios';
import { Star, ArrowUpRight } from 'lucide-react';
import { HERO, IMAGES } from '../../mock';
import { Button } from '../ui/button';
import SpecialistModal from '../SpecialistModal';
import LoginModal from '../LoginModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Hero() {
  const [live, setLive] = useState({ rate: 1.1671, delta: '+0.00%' });
  const [specialistOpen, setSpecialistOpen] = useState(false);
  const [openAccountOpen, setOpenAccountOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await axios.get(`${API}/rates`);
        const eur = res.data?.pairs?.find((p) => p.pair === 'GBP / EUR');
        if (eur && !cancelled) setLive({ rate: eur.rate, delta: eur.delta });
      } catch (_) {}
    };
    load();
    const id = setInterval(load, 15000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  const positive = String(live.delta).startsWith('+');
  const rateNum = typeof live.rate === 'number' ? live.rate.toFixed(4) : live.rate;

  return (
    <section className="relative pt-[120px] lg:pt-[140px] pb-16 lg:pb-24 overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-40 -right-40 w-[560px] h-[560px] rounded-full bg-[#0B3B2E]/6 blur-3xl" />
        <div className="absolute top-40 -left-40 w-[420px] h-[420px] rounded-full bg-[#C8944B]/15 blur-3xl" />
      </div>

      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
        <div className="lg:col-span-7">
          <h1 className="font-serif text-[52px] sm:text-[68px] lg:text-[88px] leading-[0.95] tracking-tight text-[#0B3B2E]">
            {HERO.title}
            <br />
            <span className="italic font-normal text-[#0B3B2E]/80">{HERO.titleAccent}</span>
          </h1>

          <p className="mt-7 text-[17px] lg:text-[19px] leading-relaxed text-[#0E1F1A]/70 max-w-[560px]">
            {HERO.subtitle}
          </p>

          <div className="mt-9 flex flex-wrap items-center gap-3">
            <Button
              onClick={() => setOpenAccountOpen(true)}
              className="rounded-full h-12 px-6 bg-[#0B3B2E] text-[#F5F1E8] hover:bg-[#12503F] text-[15px] font-medium group"
            >
              {HERO.primaryCta}
              <ArrowUpRight className="w-4 h-4 ml-1 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Button>
            <Button
              onClick={() => setSpecialistOpen(true)}
              variant="outline"
              className="rounded-full h-12 px-6 border-[#0B3B2E]/25 bg-transparent hover:bg-[#0B3B2E]/5 text-[#0B3B2E] text-[15px] font-medium"
            >
              {HERO.secondaryCta}
            </Button>
          </div>

          {/* Trustpilot indicator. Review count intentionally omitted;
              /api/reviews/summary will populate this later when Trustpilot is connected. */}
          <div className="mt-9 flex items-center gap-4">
            <div className="flex">
              {[0, 1, 2, 3, 4].map((i) => (
                <Star key={i} className="w-4 h-4 fill-[#C8944B] text-[#C8944B]" />
              ))}
            </div>
            <p className="text-[13px] text-[#0E1F1A]/65">
              <span className="font-semibold text-[#0B3B2E]">Excellent</span> · Rated on Trustpilot
            </p>
          </div>
        </div>

        {/* Visual: London skyline with live rate ribbon */}
        <div className="lg:col-span-5 relative">
          <div className="relative aspect-[4/5] w-full max-w-[520px] ml-auto rounded-[24px] overflow-hidden shadow-[0_30px_80px_-30px_rgba(11,59,46,0.35)]">
            <img
              src={IMAGES.hero}
              alt="London skyline at dusk"
              className="w-full h-full object-cover"
              loading="eager"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0B3B2E]/70 via-transparent to-transparent" />

            {/* Live rate ribbon */}
            <div className="absolute bottom-5 left-5 right-5 bg-[#F5F1E8]/95 backdrop-blur rounded-2xl p-5 border border-white/60">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[11px] uppercase tracking-[0.15em] text-[#0E1F1A]/55 flex items-center gap-1.5">
                    <span className="relative flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#0B3B2E] opacity-60" />
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-[#0B3B2E]" />
                    </span>
                    Live rate
                  </p>
                  <p className="font-serif text-[26px] text-[#0B3B2E] mt-1">
                    1 GBP = {rateNum} EUR
                  </p>
                </div>
                <span
                  className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${
                    positive ? 'bg-[#0B3B2E]/8 text-[#0B3B2E]' : 'bg-[#B23A2A]/10 text-[#B23A2A]'
                  }`}
                >
                  {live.delta}
                </span>
              </div>
              <div className="mt-4 h-1 rounded-full bg-[#0B3B2E]/10 overflow-hidden">
                <div
                  className="h-full bg-[#0B3B2E] rounded-full origin-left"
                  style={{ animation: 'vfx-pulse 6s ease-in-out infinite' }}
                />
              </div>
              <p className="mt-2 text-[11px] text-[#0E1F1A]/55">
                Refreshed just now · Source: interbank mid-market
              </p>
            </div>
          </div>

          {/* Floating stat chip */}
          <div className="hidden lg:flex absolute -left-8 top-16 bg-[#0B3B2E] text-[#F5F1E8] rounded-2xl px-5 py-4 shadow-xl gap-3 items-center">
            <div className="w-10 h-10 rounded-full bg-[#C8944B]/25 flex items-center justify-center">
              <span className="font-serif text-[18px] text-[#C8944B]">↓</span>
            </div>
            <div>
              <p className="font-serif text-[16px] leading-none">No</p>
              <p className="text-[11px] opacity-80 mt-1">transfer fees</p>
            </div>
          </div>
        </div>
      </div>

      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 mt-16 lg:mt-24">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-4 border-t border-[#0B3B2E]/15 pt-10">
          {HERO.metrics.map((m, i) => (
            <div key={i} className="reveal">
              <p className="font-serif text-[38px] lg:text-[48px] leading-none text-[#0B3B2E]">{m.value}</p>
              <p className="mt-2 text-[13px] text-[#0E1F1A]/65 max-w-[180px]">{m.label}</p>
            </div>
          ))}
        </div>
      </div>

      <SpecialistModal open={specialistOpen} onOpenChange={setSpecialistOpen} />
      <LoginModal open={openAccountOpen} onOpenChange={setOpenAccountOpen} defaultTab="signup" />
    </section>
  );
}
